import { supabase } from "../supabase/supabaseClient";

// Track active channels per user so we don't create duplicate subscriptions
const activeChannels = new Map();

/**
 * Get all notifications for current user
 */
export const getNotifications = async (userId, limit = 20) => {
    return await supabase
        .from("notifications")
    .select("id, user_id, type, title, body, data, created_at, read_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(limit);
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (userId) => {
    // Avoid `count: 'exact'` on large tables (can be very slow and appear to "hang").
    // For UI badge purposes, a capped count is enough.
    const { data, error } = await supabase
        .from("notifications")
        .select("id")
        .eq("user_id", userId)
        .is("read_at", null)
        .limit(101);

    if (error) return { count: 0, error };
    const count = data ? Math.min(data.length, 100) : 0;
    return { count, error: null };
};

/**
 * Mark a single notification as read
 */
export const markAsRead = async (notificationId) => {
    return await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId);
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (userId) => {
    return await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .is("read_at", null);
};

/**
 * Subscribe to realtime notifications
 * Returns the channel for cleanup
 */
export const subscribeToNotifications = (userId, onNotification) => {
    if (!userId) {
        console.warn("notificationsService.subscribeToNotifications called without userId");
        return null;
    }

    if (activeChannels.has(userId)) {
        console.debug(`notificationsService: reusing existing channel for user ${userId}`);
        return activeChannels.get(userId);
    }

    console.debug(`notificationsService: creating channel for user ${userId}`);

    const channel = supabase
        .channel(`notifications:${userId}`)
        .on(
            "postgres_changes",
            {
                event: "INSERT",
                schema: "public",
                table: "notifications",
                filter: `user_id=eq.${userId}`,
            },
            (payload) => {
                if (payload?.new) onNotification(payload.new);
            }
        )
        .subscribe();

    activeChannels.set(userId, channel);
    return channel;
};

/**
 * Unsubscribe from notifications
 */
export const unsubscribeFromNotifications = (channelOrUserId) => {
    if (!channelOrUserId) return;

    // Allow passing either the channel object or the userId string
    if (typeof channelOrUserId === "string") {
        const userId = channelOrUserId;
        const found = activeChannels.get(userId);
        if (found) {
            console.debug(`notificationsService: removing channel for user ${userId}`);
            supabase.removeChannel(found);
            activeChannels.delete(userId);
        } else {
            console.debug(`notificationsService: no active channel for user ${userId}`);
        }
        return;
    }

    const channel = channelOrUserId;
    // Try to find the userId key by value and remove mapping
    for (const [userId, ch] of activeChannels.entries()) {
        if (ch === channel) {
            console.debug(`notificationsService: removing channel for user ${userId}`);
            supabase.removeChannel(channel);
            activeChannels.delete(userId);
            return;
        }
    }

    console.debug("notificationsService: removing unknown channel");
    supabase.removeChannel(channel);
};

/**
 * Get notification route based on type and data
 */
export const getNotificationRoute = (notification) => {
    switch (notification.type) {
        case "message":
        case "conversation_request":
        case "conversation_accepted":
            return {
                path: "/messages",
                state: { conversationId: notification.data?.conversation_id },
            };
        case "favorite":
            return { path: "/profile", state: null };
        default:
            return { path: "/", state: null };
    }
};
